import { Copy, Link, Tag } from 'lucide-react';
import React from 'react';
import { useToast } from '@frontend/shadcn/hooks/use-toast';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Button } from '@frontend/shadcn/components/ui/button';
import { format } from 'date-fns';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';

interface RefreshCreatorDetailsProps {
  promoCode: string | null;
  affiliateLink: string | null | undefined;
  endDate: string | null | undefined;
}

interface PromoDataItem {
  icon: JSX.Element;
  label: string;
  value: string;
  isActive: boolean;
  color: string;
}

export default function RefreshCreatorDetails({
  promoCode,
  affiliateLink,
  endDate,
}: RefreshCreatorDetailsProps): JSX.Element {
  const { toast } = useToast();

  const promoData: PromoDataItem[] = [
    promoCode && {
      icon: <Tag size={12} className="font-bold text-blue-4" />,
      label: 'Personalized code',
      value: promoCode,
      isActive: true,
      color: 'text-blue-4',
    },
    affiliateLink && {
      icon: <Link size={12} className="font-bold text-pink-4" />,
      label: 'Shareable link',
      value: affiliateLink,
      isActive: false,
      color: 'text-pink-4',
    },
  ].filter((item): item is PromoDataItem => Boolean(item));

  const handleCopy = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        variant: 'success',
        title: 'Copied!',
        description: `${type} has been copied to clipboard`,
        duration: 2000,
      });
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Error',
        description: 'Failed to copy to clipboard',
        duration: 2000,
      });
    }
  };

  return (
    <Card className="rounded-2xl border border-grey-2 bg-grey-0 shadow-none">
      <CardContent className="flex flex-col items-start gap-4 p-4">
        <div className="relative flex w-full items-center gap-4 self-stretch overflow-y-scroll">
          <div className="relative flex flex-1 grow flex-col items-start gap-4">
            <div className="relative flex w-full flex-col items-start gap-4 self-stretch sm:flex-row">
              {promoData.map((item, index) => (
                <div
                  key={index}
                  className="relative flex w-full flex-1 grow flex-col items-start gap-2 rounded-2xl"
                >
                  <div className="relative flex w-full flex-col items-start self-stretch">
                    <div className="relative flex w-full items-center justify-between self-stretch">
                      <div className="relative inline-flex items-center gap-2">
                        <div className="flex h-3 w-3 items-center justify-center">
                          {item.icon}
                        </div>
                        <div className="font-medium-75-labels whitespace-nowrap text-grey-6">
                          {item.label}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="relative flex w-full items-center gap-3 self-stretch rounded-xl border border-solid border-grey-2 bg-grey-1 px-4 py-1.5">
                    <div className="font-medium-200-h4 whitespace-nowrap text-center text-grey-6">
                      <Tooltip>
                        <TooltipTrigger>
                          {item.value.length > 30
                            ? `${item.value.substring(0, 30)}...`
                            : item.value}
                        </TooltipTrigger>
                        <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 w-max">
                          {item.value}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1 h-8 w-8 rounded-lg"
                      aria-label="Copy to clipboard"
                      onClick={() => handleCopy(item?.value || '', item.label)}
                    >
                      <Copy size={15} className="text-grey-5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {endDate && (
              <div className="relative flex w-full items-center gap-1 self-stretch">
                <div className="font-medium-100 whitespace-nowrap text-grey-5">
                  Active till:
                </div>
                <div className="font-medium-100 whitespace-nowrap text-grey-6">
                  {format(new Date(endDate), 'MMMM d, yyyy')}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
